import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type {
  LoginResponse,
  LoginResult,
  MfaFactorType,
  RegisterRequest,
  TenantMembershipSummary,
} from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { authApi } from '@/services/auth.api';
import { registrationApi } from '@/services/registration.api';
import { tokenStore } from '@/services/api-client';

/**
 * Session state.
 *
 * Permissions are read from the access token's claims. That is safe because the
 * token is signed and the server re-checks every permission on every request —
 * the client copy exists only to decide what to render.
 *
 * Nothing here is a security boundary. A hidden button is a usability
 * improvement, not a control.
 */
interface AuthState {
  readonly userId: string | null;
  readonly tenantId: string | null;
  readonly permissions: ReadonlySet<string>;
  readonly roles: readonly string[];
  readonly memberships: readonly TenantMembershipSummary[];
  readonly isAuthenticated: boolean;
  readonly needsTenantSelection: boolean;
}

/**
 * Declared as arrow-function properties rather than method shorthand.
 *
 * Consumers destructure these (`const { login } = useAuth()`), and shorthand
 * syntax declares a method with an implicit `this` that is lost when detached
 * from its object. The arrow form says what is actually true: these are bound
 * closures.
 */
interface AuthValue extends AuthState {
  /** Resolves to a `LoginResponse` (session applied) or an `mfaRequired` result to act on. */
  readonly login: (email: string, password: string, rememberMe?: boolean) => Promise<LoginResult>;
  /** Applies a `LoginResult` `/oauth-callback` already obtained via
   *  `authApi.oauthHandoff` — the tokens are already stored by that call,
   *  this just brings React state in line with them. */
  readonly applyExternalLogin: (result: LoginResult) => void;
  /**
   * Provisions a church and, on success, applies the session exactly like
   * `login` — a fresh registration never carries an MFA requirement, but the
   * return type stays `LoginResult` so callers use the same narrowing.
   */
  readonly register: (payload: RegisterRequest) => Promise<LoginResult>;
  readonly completeMfaChallenge: (
    mfaToken: string,
    factorType: MfaFactorType,
    code: string,
    rememberMe?: boolean,
  ) => Promise<LoginResponse>;
  readonly selectTenant: (tenantId: string) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

interface TokenClaims {
  sub?: string;
  tid?: string;
  perms?: string[];
  roles?: string[];
}

/**
 * Reads the token's claims without verifying them.
 *
 * Deliberately not a signature check: the client has no key, and verifying here
 * would prove nothing anyway. A tampered token fails at the server, which is
 * the only place that matters. This decodes only to decide what to draw.
 */
function readClaims(token: string | null): TokenClaims {
  if (!token) return {};
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as TokenClaims;
  } catch {
    return {};
  }
}

function stateFromToken(memberships: readonly TenantMembershipSummary[] = []): AuthState {
  const token = tokenStore.access;
  const claims = readClaims(token);

  return {
    userId: claims.sub ?? null,
    tenantId: claims.tid ?? null,
    permissions: new Set(claims.perms ?? []),
    roles: claims.roles ?? [],
    memberships,
    isAuthenticated: Boolean(claims.sub),
    // Authenticated but with no tenant claim: the user belongs to more than one
    // church and must choose.
    needsTenantSelection: Boolean(claims.sub) && !claims.tid,
  };
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [state, setState] = useState<AuthState>(() => stateFromToken());
  const { applyProfileLocale } = useI18n();

  /** Shared by a direct login and by a login that passed an MFA challenge. */
  const applySession = useCallback(
    (response: LoginResponse) => {
      setState(stateFromToken(response.memberships));
      // Sign-in is the first moment the client knows anything about this person
      // beyond what their browser advertises. Someone whose profile says Urdu
      // should not keep reading English because the laptop is set to en-US.
      applyProfileLocale(response.user.locale);
    },
    [applyProfileLocale],
  );

  const login = useCallback(
    async (email: string, password: string, rememberMe?: boolean) => {
      const result = await authApi.login(email, password, rememberMe);
      if (!('mfaRequired' in result)) applySession(result);
      return result;
    },
    [applySession],
  );

  const applyExternalLogin = useCallback(
    (result: LoginResult) => {
      if (!('mfaRequired' in result)) applySession(result);
    },
    [applySession],
  );

  const register = useCallback(
    async (payload: RegisterRequest) => {
      const result = await registrationApi.register(payload);
      if (!('mfaRequired' in result)) applySession(result);
      return result;
    },
    [applySession],
  );

  const completeMfaChallenge = useCallback(
    async (mfaToken: string, factorType: MfaFactorType, code: string, rememberMe?: boolean) => {
      const response = await authApi.completeMfaChallenge(mfaToken, factorType, code, rememberMe);
      applySession(response);
      return response;
    },
    [applySession],
  );

  const selectTenant = useCallback(async (tenantId: string) => {
    await authApi.selectTenant(tenantId);
    setState((previous) => stateFromToken(previous.memberships));
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setState(stateFromToken([]));
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      ...state,
      login,
      applyExternalLogin,
      register,
      completeMfaChallenge,
      selectTenant,
      logout,
      /**
       * Whether to render an action. Never whether to allow one — the server
       * decides that, and it decides again on every request.
       */
      can: (permission: string) => {
        if (state.permissions.has(permission)) return true;
        // Role definitions may grant wildcards, which the server expands at
        // issuance; expanded here too so the UI matches what the API will allow.
        const segments = permission.split('.');
        for (let i = segments.length - 1; i > 0; i -= 1) {
          if (state.permissions.has(`${segments.slice(0, i).join('.')}.*`)) return true;
        }
        return false;
      },
    }),
    [state, login, applyExternalLogin, register, completeMfaChallenge, selectTenant, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside an AuthProvider');
  return value;
}
